import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from '~/lib/cx'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-live text-ground border-live hover:opacity-90',
  secondary: 'bg-raised text-text border-line hover:border-line-strong',
  ghost: 'bg-transparent text-dim border-transparent hover:text-text hover:bg-raised',
  danger: 'bg-transparent text-fault border-fault/50 hover:bg-fault/10',
}

const SIZES: Record<Size, string> = {
  sm: 'h-6 px-2 text-2xs',
  md: 'h-8 px-3 text-xs',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  /** Shown in place of the label while a mutation is in flight. */
  busy?: boolean
  icon?: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'md',
  busy = false,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cx(
        'label inline-flex shrink-0 items-center justify-center gap-1.5 rounded-sm border',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {icon}
      {busy ? 'Working…' : children}
    </button>
  )
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: the icon is the only other thing in here, and it says nothing out loud. */
  label: string
}

export function IconButton({ label, className, children, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex h-6 w-6 items-center justify-center rounded-sm text-dim',
        'transition-colors hover:bg-raised hover:text-text disabled:opacity-40',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
